import { Box } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLPaper } from "OLPaper";
import { useBasicSettingsData } from "api/basic-settings";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useSettings } from "api/general";
import { useWallets } from "api/wallets";
import { FormButtons } from "components/FormControls/FormButtons";
import { SelectInput } from "components/FormControls/Select/Select";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    OLPaper: {
      display: "flex",
      flexDirection: "column",
      marginTop: theme.spacing(8),
    },
    inputLong: {
      maxWidth: "100%",
      width: 394,
    },
    error: {
      color: theme.red500,
      display: "block",
      fontSize: 14,
      marginBottom: theme.spacing(8),
      marginTop: theme.spacing(5),
      "& + &": { marginTop: 0 },
    },
    formContainer: {
      width: "872px",
      margin: `${theme.spacing(8)} auto 0`,
      maxWidth: "100%",
    },
  }),
);

export function RewardConfiguration() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { updateSettings } = useSettings();
  const settingsData = useBasicSettingsData("rewards");
  const [walletTypeSearch, setWalletTypeSearch] = useState<string>();
  const { useInfiniteWalletsList, useWalletTypeList } = useWallets();
  const { data: initialWalletData } = useWalletTypeList({
    filters: settingsData.formValues?.rewardWalletCode
      ? [
          {
            condition: "eq",
            query: "code",
            value: settingsData.formValues.rewardWalletCode,
          },
        ]
      : [],
  });

  const {
    data: infiniteWalletsList,
    size: walletsSize,
    setSize: setWalletsSize,
    isValidating,
  } = useInfiniteWalletsList({
    filters: walletTypeSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: walletTypeSearch,
          },
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ]
      : [
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ],
    order: "asc",
    sortKey: "name",
  });

  const { infiniteData: walletsList, anyElementsToFetch: anyWalletsToFetch } =
    useManageInfiniteScroll(infiniteWalletsList || []);

  return (
    <>
      <TopHeader title={message({ id: "rewardsConfiguration" })} />
      <div className={classes.formContainer}>
        {settingsData.formValues && initialWalletData ? (
          <Formik
            enableReinitialize
            initialStatus={{}}
            initialValues={settingsData.formValues}
            onSubmit={async (values, { setSubmitting, setStatus }) => {
              setSubmitting(true);
              setStatus({});
              try {
                const response = await updateSettings(values);
                if (response) {
                  notify(message({ id: "settingsSaved" }));
                  navigate(links.rewardsList);
                }
              } catch (exception) {
                setSubmitting(false);
                notify(getErrorMessage(exception));
                const errors = backendErrors(getBackendErrors(exception));
                setStatus(errors);
                focusError();
              }
            }}
          >
            {({
              handleSubmit,
              isSubmitting,
              status,
              dirty,
              values,
              initialValues,
            }) => {
              const loadedWallet = initialWalletData?.items?.[0];
              return (
                <Form onSubmit={handleSubmit}>
                  {status?.[""] && (
                    <span className={classes.error}>{status[""]}</span>
                  )}
                  <OLPaper className={classes.OLPaper}>
                    <SelectInput
                      displaySearch
                      data-test-id="rewards-configuration-wallet-used-for-reward-redemptions-select"
                      width="100%"
                      items={walletsList.map((wallet) => ({
                        label: wallet.name,
                        id: wallet.code,
                      }))}
                      asyncSearch={(searchedQuery: string) =>
                        setWalletTypeSearch(searchedQuery)
                      }
                      initialValue={
                        loadedWallet && {
                          id: loadedWallet.code,
                          label: loadedWallet.name,
                        }
                      }
                      label={message({
                        id: "walletUsedForRewardRedemptions",
                      })}
                      name="rewardWalletCode"
                      placeholder={message({
                        id: "selectWalletType",
                      })}
                      loadMoreElements={() =>
                        !isValidating && setWalletsSize(walletsSize + 1)
                      }
                      anyElementsToFetch={anyWalletsToFetch}
                    />
                  </OLPaper>
                  <Box mt={12} />
                  <FormButtons
                    disableSubmit={isSubmitting}
                    onCancelClick={() => navigate(links.rewardsList)}
                    onSubmitClick={() => handleSubmit()}
                  />
                  {!isSubmitting && (
                    <LeaveSiteGuard
                      warnBeforeRefresh
                      isDirty={dirty}
                      initialValues={initialValues}
                      currentValues={values}
                    />
                  )}
                </Form>
              );
            }}
          </Formik>
        ) : (
          <>
            <Skeleton height={300} width={"100%"} />
            <Box mt={12} />
            <FormButtons
              disableSubmit
              onCancelClick={() => {}}
              onSubmitClick={() => {}}
            />
          </>
        )}
      </div>
    </>
  );
}
